import {
  BugReport as BugReportIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Typography,
} from '@mui/material'
import { Fragment, useEffect, useState } from 'react'
import {
  EmailField,
  Show,
  SimpleShowLayout,
  UrlField,
  useGetList,
  usePermissions,
  useRedirect,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { Link as RouterLink } from 'react-router-dom'
import { AlertContextBanner, SimpleAlertPanel, useAlertContext } from '../alerts'
import AssignVetButton from '../clinic-team/AssignVetButton'
import PetAlertMuteSwitch from '../clinic-team/PetAlertMuteSwitch'
import ToneChip from '../components/ToneChip'
import { PetGlucoseDetailPanel, PetTreatmentCard } from '../dashboard/components'
import { useDeviceLastReadings } from '../hooks/useDeviceLastReadings'
import { deviceHasMetrics, useDevicesWithMetrics } from '../hooks/useDevicesWithMetrics'
import { usePetCoOwners } from '../hooks/usePetCoOwners'
import type { Device } from '../types'
import { hardRefresh } from '../utils/hardRefresh'
import { formatDobForDisplay } from './dob'
import HidePetButton from './HidePetButton'
import { derivePetDataStatus, type PetDataStatus, petDataStatusTone } from './petDataStatus'

// If neither async source (this pet's devices, the devices-with-metrics
// membership set) has settled after this long, the page would otherwise sit
// silently blank below the alerts. That silent-blank state is what generated a
// run of after-hours "the chart is gone" support calls (2026-07-13): the
// affected browsers were running an OLD cached bundle whose gate hung here with
// no explanation, and the only known cure was a manual cache clear. The current
// build already renders a status banner for every *settled* outcome, so the one
// remaining way to blank is a never-settling fetch (e.g. /devices-with-metrics
// held open with no client-side timeout). Surface a self-serve Reload instead
// of a blank page. 10s is comfortably past a healthy cache-hit settle (<1s) so
// this never flashes during normal loads.
const LOAD_TIMEOUT_MS = 10_000

// Roles that get the internal debug panel. Mirrors the auth-side
// INTERNAL_STAFF_ROLES set used by /api/users/clinic-search and
// /api/users/clinic-linked-pets — kept in sync because the debug section
// surfaces IDs that vets/owners shouldn't be reading.
const INTERNAL_STAFF_ROLES = new Set([
  'admin',
  'cms',
  'it',
  'vetadmin',
  'siteadmin',
  'account_manager',
])

const formatRelativeTime = (dateInput: Date | string | number | null | undefined): string => {
  if (dateInput == null) return '—'
  const ms =
    typeof dateInput === 'number'
      ? dateInput * (dateInput < 10_000_000_000 ? 1000 : 1) // seconds vs ms
      : new Date(dateInput).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return '—'
  const deltaSec = Math.floor((Date.now() - ms) / 1000)
  if (deltaSec < 60) return `${deltaSec}s ago`
  if (deltaSec < 60 * 60) return `${Math.floor(deltaSec / 60)}m ago`
  if (deltaSec < 24 * 60 * 60) return `${Math.floor(deltaSec / 3600)}h ago`
  return `${Math.floor(deltaSec / 86400)}d ago`
}

const PetShowContent = () => {
  const translate = useTranslate()
  const { record } = useShowContext()
  // The pet record carries `dob` (never `birthDate`, which no backend returns).
  // formatDobForDisplay owns the timezone rule: dob is a calendar day, so it is
  // shown as that day in every zone.
  const birthday = formatDobForDisplay(record?.dob)
  const petId = record?.id
  const { hasAlertContext } = useAlertContext()
  const { permissions } = usePermissions()
  const isInternalStaff = INTERNAL_STAFF_ROLES.has(permissions?.role)
  const [debugOpen, setDebugOpen] = useState(false)
  const redirect = useRedirect()

  const { devicesWithMetrics, isLoading: metricsMembershipLoading } = useDevicesWithMetrics()
  const { lastReadings } = useDeviceLastReadings()
  // vic/vetpo#65: app users linked to a vet-owned pet. Only the owner-of-record
  // vet is answered (others get 403 → empty), so don't even ask other roles.
  const coOwners = usePetCoOwners(petId, permissions?.role === 'vet')

  // Fetch devices for this pet
  const {
    data: devices = [],
    isLoading: devicesLoading,
    error: devicesError,
  } = useGetList<Device>('devices', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'ASC' },
    filter: { pet: petId },
  })

  // Filter to devices with metrics data, then pick the first active or first available
  const devicesFiltered = devices.filter((d) => deviceHasMetrics(d.id, devicesWithMetrics))
  const activeDevice = devicesFiltered.find((d) => (d as any).active) || devicesFiltered[0]
  const deviceId = activeDevice?.id

  // The device list and the "which devices have metrics" membership set resolve
  // on independent async timelines, and deviceHasMetrics() treats a
  // still-loading membership set as "show all" (null → true). If we rendered
  // the status banner / chip / heavy detail panel off those values while either
  // source was mid-flight, the page would mount content on the optimistic
  // fallback and then re-collapse it once the real data arrived — a visible
  // flicker on the pet page, worst for 0-metric "waiting sync" sensors where
  // the panel flashes in and back out. Hold every derived-status region behind
  // a single "both settled" gate so the page transitions at most once.
  const dataStatusSettled = !metricsMembershipLoading && !devicesLoading

  // A devices list that FAILED (after the app-wide retries) arrives as [], which
  // derives to 'no_sensor': the page said "No sensor — add one" and offered Add
  // Sensor for a patient whose sensors simply had not loaded. Say the list failed
  // instead, and show no derived status (eng-brain pass 78).
  const devicesFailed = !!devicesError

  // Watchdog for a never-settling load (see LOAD_TIMEOUT_MS). Arm the timer
  // only while unsettled; clear + reset the moment it settles so a slow-but-
  // successful load never leaves the banner up.
  const [loadTimedOut, setLoadTimedOut] = useState(false)
  useEffect(() => {
    if (dataStatusSettled) {
      setLoadTimedOut(false)
      return
    }
    const timer = window.setTimeout(() => setLoadTimedOut(true), LOAD_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [dataStatusSettled])

  // Derive the canonical data status for this pet — drives the empty state
  // message AND the small status chip in the header.
  // NOT activeDevice.start_time. That is the sensor ACTIVATION time and a lower
  // bound on every metric, so `now - it` measures sensor AGE: it drove a
  // healthy 10-day-old sensor to `data_error`, which this page then renders by
  // HIDING the status chip entirely (see the chip's `!== 'data_error'` gate).
  // Absent from the map = unknown (loading / failed), which derives to active.
  const activeDeviceLastReading = activeDevice ? lastReadings?.get(activeDevice.id) : undefined
  const latestMetricTime = activeDeviceLastReading
    ? Math.floor(activeDeviceLastReading.getTime() / 1000)
    : null
  const dataStatus: PetDataStatus = derivePetDataStatus({
    pet: record as any,
    devices,
    devicesWithMetrics: devicesWithMetrics ?? null,
    latestMetricTime,
  })

  const renderEmptyState = () => {
    switch (dataStatus) {
      case 'no_sensor':
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>{translate('resources.pets.data_status_alert.no_sensor.title')}</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {translate('resources.pets.data_status_alert.no_sensor.body')}
            </Typography>
            <Button
              size="small"
              variant="contained"
              component={RouterLink}
              to={`/devices/create?pet=${petId}`}
            >
              {translate('resources.pets.data_status_alert.no_sensor.action')}
            </Button>
          </Alert>
        )
      case 'waiting_sync':
        return (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>
              {translate('resources.pets.data_status_alert.waiting_sync.title')}
            </AlertTitle>
            <Typography variant="body2">
              {translate('resources.pets.data_status_alert.waiting_sync.body', {
                smart_count: devices.length,
              })}
            </Typography>
          </Alert>
        )
      case 'data_error':
        // Temporarily hidden per product request (2026-06-26): suppress the
        // "Sensor data has stalled" banner on the pet detail page.
        return null
      case 'expired':
        // Shown in place of the "no recent data" / stalled-connection reading
        // a vet would otherwise see once ada-core#293 excludes post-expiry
        // readings from every read (vic/vetpo#63): the sensor did not go
        // silent, it finished its 14-day + 6h wear life and needs replacing.
        return (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>{translate('resources.pets.data_status_alert.expired.title')}</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {translate('resources.pets.data_status_alert.expired.body')}
            </Typography>
            <Button
              size="small"
              variant="contained"
              component={RouterLink}
              to={`/devices/create?pet=${petId}`}
            >
              {translate('resources.pets.data_status_alert.expired.action')}
            </Button>
          </Alert>
        )
      case 'archived':
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>{translate('resources.pets.data_status_alert.archived.title')}</AlertTitle>
            <Typography variant="body2">
              {translate('resources.pets.data_status_alert.archived.body')}
            </Typography>
          </Alert>
        )
      default:
        return null
    }
  }

  const renderDebugPanel = () => {
    if (!isInternalStaff || !record) return null
    const ownerId = (record as any).owner?.id ?? (record as any).owner ?? null
    const ownerVets = (record as any).owner?.vets
    const linkedVetIds = Array.isArray(ownerVets)
      ? ownerVets.map((v: any) => (typeof v === 'object' ? v?.id : v)).filter(Boolean)
      : []
    const totalReadings = devicesFiltered.reduce(
      (acc: number, d: Device) => acc + ((d as any).metricsCount ?? 0),
      0,
    )
    const debugRows: Array<[string, React.ReactNode]> = [
      ['Pet ID', record.id],
      ['Owner ID', ownerId ?? '—'],
      ['Linked Vet/Clinic IDs', linkedVetIds.length ? linkedVetIds.join(', ') : '—'],
      ['Total devices', devices.length],
      ['Devices with metrics', devicesFiltered.length],
      ['Current Sensor ID', activeDevice?.id ?? '—'],
      [
        'Current Sensor model',
        activeDevice?.model || activeDevice?.platform || activeDevice?.type || '—',
      ],
      [
        'Last Sensor Sync',
        latestMetricTime
          ? `${new Date(latestMetricTime * 1000).toISOString()} (${formatRelativeTime(latestMetricTime)})`
          : '—',
      ],
      ['Total Readings (best-effort)', totalReadings || '—'],
      [
        'Session Status',
        activeDevice ? ((activeDevice as any).active === false ? 'inactive' : 'active') : '—',
      ],
      [
        'Pet Status',
        (record as any).status === -1
          ? 'archived (-1)'
          : (record as any).status === 0
            ? 'active (0)'
            : String((record as any).status ?? '—'),
      ],
    ]

    return (
      <Accordion
        expanded={debugOpen}
        onChange={() => setDebugOpen((v) => !v)}
        sx={{ mt: 3 }}
        data-testid="pet-debug-panel"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <BugReportIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="subtitle2">Debug info (internal staff only)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="dl"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: '4px 16px',
              m: 0,
              fontSize: '0.85rem',
            }}
          >
            {debugRows.map(([label, value]) => (
              <Box key={label} sx={{ display: 'contents' }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, wordBreak: 'break-all' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <SimpleShowLayout sx={{ p: 0, '&:first-of-type': { pt: 0 } }}>
      {/* Patient header. It was a flat row of raw fields — "303954  Test Pet
          Labrador  user2@adapet.ai" at body size, with Edit floating above the
          card and Hide at the far end — so the page had no title at all. Name
          is the title; the rest is one muted meta line; actions sit together. */}
      <Box
        className="report-header"
        data-testid="pet-show-header"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.5, md: 3 },
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Typography variant="h5" component="h1" sx={{ wordBreak: 'break-word' }}>
              {record?.name}
            </Typography>
            {/* "Data Error" status badge temporarily hidden per product request
                (2026-06-26). Other statuses still surface their badge. Gated on
                dataStatusSettled so it doesn't flash the wrong status mid-load. */}
            {dataStatusSettled && !devicesFailed && dataStatus !== 'data_error' && (
              <ToneChip
                tone={petDataStatusTone(dataStatus)}
                data-testid="pet-data-status"
                // Translated label — this used to print the raw enum
                // ("waiting sync") even in the Chinese UI.
                label={translate(`resources.pets.data_status.${dataStatus}`, {
                  _: dataStatus.replace('_', ' '),
                })}
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 1,
              rowGap: 0.25,
              mt: 0.5,
              color: 'text.secondary',
              '& > *:not(:last-child)::after': { content: '"·"', ml: 1, color: 'text.disabled' },
            }}
          >
            {record?.breed && <Typography variant="body2">{record.breed}</Typography>}
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {translate('resources.pets.fields.id')} {record?.id}
            </Typography>
            {birthday && (
              <Typography variant="body2" data-testid="pet-birthday">
                {translate('resources.pets.fields.birthday')} {birthday}
              </Typography>
            )}
            {(record as any)?.owner?.email && (
              <Typography variant="body2">
                {translate('resources.pets.fields.owner')}{' '}
                <EmailField source="owner.email" variant="body2" />
              </Typography>
            )}
            {(record as any)?.owner?.website && <UrlField source="owner.website" />}
            {coOwners.length > 0 && (
              <Typography variant="body2" data-testid="pet-co-owners">
                {translate('resources.pets.fields.co_owners')}{' '}
                {coOwners.map((c, i) => (
                  <Fragment key={c.id}>
                    {i > 0 && '; '}
                    {[c.name, c.email].filter(Boolean).join(' · ')}
                  </Fragment>
                ))}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {petId != null && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              // A plain hash-route link (the app uses a hash router): keeps
              // open-in-new-tab and needs no router context.
              href={`#/pets/${petId}`}
            >
              {translate('ra.action.edit')}
            </Button>
          )}
          {/* vic/vetpo#13: the one page every vet reaches (dashboard → View),
              so "Hide from my list" lives here. Renders only for role 'vet'.
              Once hidden, this clinic can no longer read the pet, so leave
              for the dashboard; the Undo notice is app-level and survives. */}
          {/* vic/vetpo#68: same move for Assign vet — master accounts only
              (the button renders null otherwise); refresh() re-reads the pet so
              the responsible vet shown beside it updates after a save. */}
          <AssignVetButton header />
          {/* vic/vetpo#70: my own alerts for this patient on/off. */}
          {(permissions?.role === 'vet' || permissions?.role === 'vetadmin') && (
            <PetAlertMuteSwitch petId={petId ?? null} />
          )}
          <HidePetButton onHidden={() => redirect('/')} />
        </Box>
      </Box>

      {/* Never end the page silently: if the data gates never settle (a stale
          cached bundle hanging on /devices-with-metrics, the 2026-07-13
          after-hours "chart is gone" calls), offer a self-serve hard reload
          rather than a blank page below the alerts. hardRefresh() busts caches +
          unregisters SWs, which is exactly what those callers were doing by hand. */}
      {!dataStatusSettled && loadTimedOut && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void hardRefresh()}>
              {translate('resources.pets.data_status_alert.load_timeout.action')}
            </Button>
          }
        >
          <AlertTitle>
            {translate('resources.pets.data_status_alert.load_timeout.title')}
          </AlertTitle>
          <Typography variant="body2">
            {translate('resources.pets.data_status_alert.load_timeout.body')}
          </Typography>
        </Alert>
      )}

      {dataStatusSettled &&
        (devicesFailed ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>
              {translate('resources.pets.data_status_alert.devices_load_failed.title')}
            </AlertTitle>
            <Typography variant="body2">
              {translate('resources.pets.data_status_alert.devices_load_failed.body')}
            </Typography>
          </Alert>
        ) : (
          renderEmptyState()
        ))}

      {/* Alert Context Banner */}
      {hasAlertContext && (
        <Box sx={{ mb: 2 }}>
          <AlertContextBanner />
        </Box>
      )}

      {/* Alerts - Hide for siteadmin (read-only access) */}
      {permissions?.role !== 'siteadmin' && (
        <Box sx={{ mb: 2 }}>
          <SimpleAlertPanel
            petId={petId}
            compact={false}
            pollingInterval={5 * 60 * 1000} // Poll every 5 minutes for alerts
          />
        </Box>
      )}

      {/* Comprehensive glucose detail (Parts 2-7): device picker, summary +
          TIR, trend, daily TIR, AGP + vet notes, daily trends, low-glucose
          events, and CSV/PDF export. Shown only when the pet has a device
          with metrics. */}
      {dataStatusSettled && deviceId && (
        <Box mt={2}>
          <PetGlucoseDetailPanel petId={petId} />
        </Box>
      )}

      {/* Hardening (2026-07-10 it-role incident): if the pet HAS devices but
          NONE are chart-eligible (empty/non-covering devices-with-metrics
          membership — an access/scoping inconsistency, since the pet record
          itself was readable) the panel gate above renders nothing, and the
          statuses that would explain it (no_sensor / waiting_sync / archived)
          don't apply — the page just ended silently below the alerts. That
          silence cost a full day of field debugging twice (Royal City PAWS,
          then the it-role hunt). Never end the page silently: say what state
          the data is in. */}
      {dataStatusSettled &&
        !deviceId &&
        devices.length > 0 &&
        !['no_sensor', 'waiting_sync', 'archived', 'expired'].includes(dataStatus) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>
              {translate('resources.pets.data_status_alert.device_data_unavailable.title')}
            </AlertTitle>
            <Typography variant="body2">
              {translate('resources.pets.data_status_alert.device_data_unavailable.body')}
            </Typography>
          </Alert>
        )}

      {/* No chart data (no sensor yet, waiting for first sync, between
          sensors): the detail panel above — and the Treatment Plan + Vet
          Notes column inside it — never mounts, yet this is exactly when a
          vet writes a plan for a newly diagnosed patient. Archived pets get
          no editor. */}
      {dataStatusSettled && !deviceId && petId != null && dataStatus !== 'archived' && (
        <PetTreatmentCard petId={petId} />
      )}

      {renderDebugPanel()}
    </SimpleShowLayout>
  )
}

const PetShow = (props: any) => {
  return (
    // actions={false}: Edit now sits in the patient header with Hide, instead
    // of floating alone above the card.
    // The Show card is a plain frame: every section inside (alerts, devices,
    // summary, charts) is already its own card, so the outer border made the
    // whole patient page a card-in-a-card, inset from the page gutter.
    <Show
      title={false}
      actions={false}
      sx={{
        '& .RaShow-card': {
          border: 0,
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          // A flex item: with overflow no longer hidden, its automatic min
          // width became its content's min-content (435px on a 390 phone),
          // pushing every section card past the viewport. Keep it shrinkable.
          minWidth: 0,
        },
      }}
      {...props}
    >
      <PetShowContent />
    </Show>
  )
}

export default PetShow
